import './App.css';
import './index.scss';
import React, { useState, useEffect, useReducer, useCallback, useRef } from 'react';
import WelcomeModeScreen from './components/WelcomeModeScreen';
import CardsHomeScreen from './components/cards/CardsHomeScreen';
import SettingsScreen from './components/SettingsScreen';
import StatsScreen from './components/StatsScreen';
import { STUDY_CATEGORIES } from "./constants/categories";
import { initialCardState, cardReducer } from "./components/cards/CardReducer";
import {
  STARTING_RATING,
  calculateNewRating,
  getCategoryKFactor,
} from "./utils/elo";
import {
  ensureRatingHistoryBaseline,
  saveRatingSnapshot,
} from "./utils/ratingHistory";
import {
  DEFAULT_BUZZER_SESSION_LENGTH,
  DEFAULT_STUDY_SESSION_LENGTH,
} from "./constants/sessionSettings";

// Used in full game mode only
export const ScoreContext = React.createContext();
export const StartTimerContext = React.createContext();
export const PlayerContext = React.createContext();
export const GameInfoContext = React.createContext();

let questions = [];

const clueLoadErrorMessages = {
  offline: {
    title: "You appear to be offline.",
    body: "Check your internet connection and try again.",
  },
  serverUnavailable: {
    title: "Trivia server is temporarily unavailable.",
    body: "The clue server may be paused, restarting, or undergoing maintenance. Please try again later.",
  },
  rateLimit: {
    title: "You’re going a little too fast!",
    body: "Please wait a moment and try again.",
  },
  noClues: {
    title: "No clues found.",
    body: "Try a different category, difficulty, or start a new session.",
  },
  badRequest: {
    title: "Something went wrong with the clue request.",
    body: "Please refresh the page and try again.",
  },
  forbidden: {
    title: "This request is not available right now.",
    body: "Please refresh the page and try again.",
  },
  generic: {
    title: "Couldn’t load clues right now.",
    body: "The trivia server may be temporarily unavailable. Please try again in a moment.",
  },
};

class ClueLoadError extends Error {
  constructor(type, message = "Failed to load clues") {
    super(message);
    this.name = "ClueLoadError";
    this.type = type;
  }
}


const App = () => {
  const [cardState, dispatchCard] = useReducer(cardReducer, initialCardState);
  const [loading, setLoading] = useState(false);
  const [clueLoadError, setClueLoadError] = useState(null);
  const ratingSnapshotSavedRef = useRef(false);
  const [ratings, setRatings] = useState(() => {
    const saved = localStorage.getItem("alatriviaRatings");

    if (saved) {
      try {
        const parsed = JSON.parse(saved);

        return {
          overall:
            parsed.overall ?? STARTING_RATING,

          categories:
            parsed.categories ?? {},
        };
      } catch {
        // Fall through to defaults
      }
    }

    return {
      overall: STARTING_RATING,
      categories: {},
    };
  });

  const API_URL = process.env.REACT_APP_API_URL;

  const [selectedCategoryIds, setSelectedCategoryIds] = useState(() => {
    const saved = localStorage.getItem("studyCategoryIds");

    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        // Fall through to defaults
      }
    }
    return STUDY_CATEGORIES.map((category) => category.id);
  });

  const [buzzerSessionLength, setBuzzerSessionLength] =
    useState(() => {
      const stored = localStorage.getItem(
        "alatriviaBuzzerSessionLength"
      );

      return stored
        ? Number(stored)
        : DEFAULT_BUZZER_SESSION_LENGTH;
    });

  const [studySessionLength, setStudySessionLength] =
    useState(() => {
      const stored = localStorage.getItem(
        "alatriviaStudySessionLength"
      );

      return stored
        ? Number(stored)
        : DEFAULT_STUDY_SESSION_LENGTH;
    });

  const canStartStudy = selectedCategoryIds.length > 0;
  const [sessionStartRatings, setSessionStartRatings] = useState(null);


  useEffect(() => {
    localStorage.setItem(
      "studyCategoryIds",
      JSON.stringify(selectedCategoryIds)
    );
  }, [selectedCategoryIds]);

  useEffect(() => {
    if (
      cardState.currentStreak == null ||
      cardState.bestStreak == null ||
      cardState.consecutivePasses == null
    ) {
      return;
    }

    localStorage.setItem("currentStreak", cardState.currentStreak);
    localStorage.setItem("bestStreak", cardState.bestStreak);
    localStorage.setItem("consecutivePasses", cardState.consecutivePasses);

  }, [cardState.currentStreak, cardState.bestStreak, cardState.consecutivePasses]);

  useEffect(() => {
    localStorage.setItem(
      "alatriviaRatings",
      JSON.stringify(ratings)
    );
  }, [ratings]);

  useEffect(() => {
    ensureRatingHistoryBaseline(ratings);
  }, [ratings]);

  useEffect(() => {
    if (
      cardState.phase !== "complete" ||
      !cardState.ratingEligible ||
      ratingSnapshotSavedRef.current
    ) {
      return;
    }

    saveRatingSnapshot(ratings);

    ratingSnapshotSavedRef.current = true;
  }, [
    cardState.phase,
    cardState.ratingEligible,
    ratings,
  ]);

  useEffect(() => {
    localStorage.setItem(
      "alatriviaBuzzerSessionLength",
      String(buzzerSessionLength)
    );
  }, [buzzerSessionLength]);

  useEffect(() => {
    localStorage.setItem(
      "alatriviaStudySessionLength",
      String(studySessionLength)
    );
  }, [studySessionLength]);

  async function loadClues(mode) {
    setLoading(true);

    try {
      setClueLoadError(null);

      const limit = mode === "decision" ? buzzerSessionLength : studySessionLength;
      const streak = mode === "decision" ? cardState.currentStreak : 0;
      const recentClueIds = JSON.parse(localStorage.getItem("recentClueIds") || "[]");

      const params = new URLSearchParams({
        limit: String(limit),
        streak: String(streak),
        excludeIds: recentClueIds.join(","),
      });

      if (mode === "review") {
        params.set(
          "categoryIds",
          selectedCategoryIds.join(",")
        );
      }

      if (
        mode === "review" &&
        selectedCategoryIds.length === 0
      ) {
        return;
      }

      const res = await fetch(
        `${API_URL}/api/clues?${params.toString()}`
      );

      const contentType = res.headers.get("content-type") || "";

      if (!res.ok) {
        if (res.status === 400) {
          throw new ClueLoadError("badRequest");
        }

        if (res.status === 403) {
          if (contentType.includes("text/html")) {
            throw new ClueLoadError("serverUnavailable");
          }

          throw new ClueLoadError("forbidden");
        }

        if (res.status === 429) {
          throw new ClueLoadError("rateLimit");
        }

        if ([500, 502, 503, 504].includes(res.status)) {
          throw new ClueLoadError("serverUnavailable");
        }

        throw new ClueLoadError("generic");
      }

      const questions = await res.json();

      if (!Array.isArray(questions) || questions.length === 0) {
        throw new ClueLoadError("noClues");
      }

      // store the most recent 100 clue ids to prevent duplicates
      const newIds = questions.map((q) => q.id);
      const updatedRecentIds = [
        ...new Set([
          ...newIds,
          ...recentClueIds,
        ]),
      ].slice(0, 100);

      localStorage.setItem("recentClueIds", JSON.stringify(updatedRecentIds));
      ratingSnapshotSavedRef.current = false;

      setSessionStartRatings({
        overall: ratings.overall,
        categories: Object.fromEntries(
          Object.entries(ratings.categories).map(
            ([categoryId, categoryData]) => [
              categoryId,
              { ...categoryData },
            ]
          )
        ),
      });

      dispatchCard({
        type: "START_SESSION",
        mode,
        questions,
      });
    } catch (err) {
      console.error("Failed to load clues:", err);

      if (!navigator.onLine) {
        setClueLoadError("offline");
        return;
      }

      setClueLoadError(err.type || "generic");
    } finally {
      setLoading(false);
    }
  }

  const recordRatedAttempt = useCallback(
    (question, result) => {
      if (!question) return;

      const categoryId = String(
        question.category_id
      );

      setRatings((previous) => {
        const currentOverallRating =
          previous.overall ??
          STARTING_RATING;

        const existingCategory =
          previous.categories?.[categoryId];

        const currentCategoryRating =
          existingCategory?.rating ??
          STARTING_RATING;

        const categoryAttempts =
          existingCategory?.attempts ?? 0;

        const categoryKFactor =
          getCategoryKFactor(categoryAttempts);

        const newOverallRating =
          calculateNewRating(
            currentOverallRating,
            question.difficulty,
            result,
            categoryKFactor
          );

        const newCategoryRating =
          calculateNewRating(
            currentCategoryRating,
            question.difficulty,
            result,
            categoryKFactor
          );

        return {
          ...previous,
          overall: newOverallRating,
          categories: {
            ...previous.categories,
            [categoryId]: {
              rating: newCategoryRating,
              attempts: categoryAttempts + 1
            },
          },
        };
      });
    },
    []
  );

  const errorMessage = clueLoadError
    ? clueLoadErrorMessages[clueLoadError] || clueLoadErrorMessages.generic
    : null;

  let content;
  if (cardState.screen === "menu") {
    content = (
      <WelcomeModeScreen
        errorMessage={errorMessage}
        onOpenStats={() => {
          dispatchCard({ type: "OPEN_STATS" });
        }}
        onOpenSettings={() => {
          dispatchCard({ type: "OPEN_SETTINGS" });
        }}
        onSelectMode={loadClues}
        canStartStudy={canStartStudy}
      />

    );
  } else if (cardState.screen === "session") {
    content = (
      <CardsHomeScreen
        state={cardState}
        dispatch={dispatchCard}
        mode="buzzer"
        questions={questions}
        loadQuestions={loadClues}
        onRatedAttempt={recordRatedAttempt}
        ratings={ratings}
        sessionStartRatings={sessionStartRatings}
      />
    );
  } else if (cardState.screen === "settings") {
    content = (
      <SettingsScreen
        selectedCategoryIds={selectedCategoryIds}
        onChange={setSelectedCategoryIds}
        buzzerSessionLength={buzzerSessionLength}
        onBuzzerSessionLengthChange={setBuzzerSessionLength}
        studySessionLength={studySessionLength}
        onStudySessionLengthChange={setStudySessionLength}
        onBack={() =>
          dispatchCard({ type: "EXIT_SETTINGS" })
        }
      />
    );
  } else if (cardState.screen === "stats") {
    content = (
      <StatsScreen
        ratings={ratings}
        categories={STUDY_CATEGORIES}
        onBack={() =>
          dispatchCard({ type: "EXIT_TO_MENU" })
        }
      />
    );
  }

  return (
    <>
      {loading && (
        <div className="loading-overlay">
          <div className="spinner" />
          <div className="loading-text">
            Loading clues...
          </div>
        </div>
      )}

      {content}
    </>
  );
};

export default App;
